/*
 * The frame every signed-in screen sits in (spec 12 §4.2). Up to four landmarks — rail, sidebar,
 * main, details — laid out in one grid, a skip link past the navigation, one connection banner, and
 * the three overlays that belong to no screen in particular: the quick switcher, the keyboard sheet
 * and — below 768 px, where the sidebar is not a column — the conversation list as a sheet.
 *
 * Three decisions are worth knowing. The layout is decided once, here, from the window width, so
 * the panes cannot disagree about which of the three layouts is current. F6 cycles the regions in
 * DOM order, which is the standard way past a pane a keyboard cannot escape from, and it is a
 * registered shortcut like every other. And the sidebar is on every place (VISUAL.md §7.13, D15) —
 * a conversation, Activity, Search, the directory, Settings, Administration, the 404 — so that a
 * move between two screens never shifts the interface sideways by a column; only the details
 * column comes and goes with what the reader is doing, because it exists only while something has
 * been asked to be shown in it (UX §2) — as a column while there is room for one, as a sheet when
 * there is not.
 *
 * The conversation list is the same story read from the other end. Below 768 px it is no longer
 * beside the screen, so the bar's *Chats* entry shows it over the screen, in the sheet the details
 * column already uses — same sections, same rows, same counts, because it is the same `Sidebar`
 * (VISUAL.md §7.19, X-B3 A1). Choosing a row navigates and the sheet is gone.
 *
 * Two screens ask the frame for room, and the frame gives it without learning their layout. A place
 * may fold its column beside a page on a window too narrow for both (`SidebarSection.fold`, G-02
 * §2): the column is then not a track of the grid, and the page's own toggle brings it back *over*
 * the page for a look (`useColumnFold`, `useOverlayColumn`). And below 768 px a screen whose whole
 * height is somebody's writing may ask the bottom bar to step aside (`useHideBottomBar`).
 */

import { useNavigate, useRouter, useRouterState } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { Button, Dialog } from "../../ui/index.ts"
import { t, translate } from "../i18n/index.ts"
import type { DetailsView, ShortcutDef, SidebarSection } from "../modules/registry.ts"
import { useSlot } from "../modules/slots.ts"
import type { ConversationId } from "../store/model.ts"
import { selectSetting } from "../store/selectors.ts"
import { ConnectionBanner } from "./ConnectionBanner.tsx"
import { ActiveConversationProvider, useModel } from "./context.tsx"
import { DetailsProvider, useDetails } from "./details.tsx"
import { DetailsPane } from "./DetailsPane.tsx"
import { Icon } from "./icons.tsx"
import {
	BottomBarContext,
	DETAILS,
	DETAILS_WIDTH_KEY,
	MAIN_ID,
	SIDEBAR,
	SIDEBAR_ID,
	SIDEBAR_WIDTH_KEY,
	useLayoutMode,
	useWindowAtLeast,
} from "./layout.ts"
import {
	foldRuleOf,
	placeOf,
	readFoldChoice,
	restingColumn,
	sectionsInPlace,
	SidebarFoldContext,
	toggledColumn,
	writeFoldChoice,
	type ColumnState,
	type FoldChoice,
	type SidebarFold,
} from "./place.ts"
import { QuickSwitcher } from "./QuickSwitcher.tsx"
import { Rail } from "./Rail.tsx"
import { ResizeHandle, useResizablePanel } from "./ResizablePanel.tsx"
import "./shell.css"
import { ShortcutProvider } from "./ShortcutProvider.tsx"
import { coreShortcuts } from "./shortcuts.ts"
import { ShortcutSheet } from "./ShortcutSheet.tsx"
import { Sidebar } from "./Sidebar.tsx"
import { TOAST_FLOOR } from "./ToastHost.tsx"

/** The grid and its landmarks, named once: F6 reads the regions back out of the grid. */
const GRID = "tb-shell__grid"
const REGION = "tb-region"

interface ShellProps {
	children: ReactNode
	/**
	 * Core's own details views. They are handed in rather than listed here because the shell is what
	 * a view is rendered by: a list inside `core/shell/` would import the areas that import the
	 * shell (UX §2.1). A module's views arrive through the `detailsViews` slot instead.
	 */
	views?: DetailsView[] | undefined
}

export function Shell({ children, views }: ShellProps) {
	return (
		<DetailsProvider>
			<ShellFrame views={views}>{children}</ShellFrame>
		</DetailsProvider>
	)
}

function ShellFrame({ children, views }: ShellProps) {
	const navigate = useNavigate()
	// TanStack types the router through the *registered* router type, and this application registers
	// none (routes/routes.tsx says why); the one thing needed from it here is a step back.
	const router = useRouter() as unknown as { history: { back: () => void } }
	const mode = useLayoutMode()
	const [switcherOpen, setSwitcherOpen] = useState(false)
	const [sheetOpen, setSheetOpen] = useState(false)
	const [chatsOpen, setChatsOpen] = useState(false)
	const [activeConversation, setActiveConversation] = useState<ConversationId | null>(null)

	const pathname = useRouterState({ select: (state) => state.location.pathname })
	const details = useDetails()

	// The column exists wherever a section does — which, for a section that names no place, is
	// everywhere; a shell with no module registered has none. On an address whose place folds it,
	// it may stand, be folded away or lie over the page (`useColumnFold` below).
	const place = placeOf(pathname)
	const sidebarSections = sectionsInPlace(useSlot("sidebarSections"), place)
	const hasColumn = mode !== "narrow" && sidebarSections.length > 0
	const { fold, keep: keepColumn } = useColumnFold(
		hasColumn ? sidebarSections : [],
		place,
		pathname,
	)
	const hasSidebar = hasColumn && fold.state === "docked"
	const overlayRegion = useOverlayColumn(fold)

	// Below 768 px a screen may ask the bottom bar to step aside (`useHideBottomBar`); the requests
	// are counted, so each asker withdraws its own and none undoes another's.
	const [barAsks, setBarAsks] = useState(0)
	const stepAside = useCallback(() => {
		setBarAsks((count) => count + 1)
		return () => setBarAsks((count) => count - 1)
	}, [])
	const hasRail = mode !== "narrow" || barAsks === 0

	// Which view the open request names. A request for a view no module contributes (a module
	// switched off between two releases) shows nothing rather than an error.
	const moduleViews = useSlot("detailsViews")
	const openView = useMemo(() => {
		if (details.request === null) return undefined
		const id = details.request.view
		return [...moduleViews, ...(views ?? [])].find((view) => view.id === id)
	}, [details.request, moduleViews, views])
	const hasDetails = mode === "wide" && openView !== undefined

	// A thread is not a detail of another conversation and a profile not of another page: leaving
	// the page closes the column. A change of the search string does not — that is where the open
	// thread writes itself (UX §2.3).
	const closeDetails = useCallback(() => details.close(), [details])
	const previousPath = useRef(pathname)
	useEffect(() => {
		if (previousPath.current === pathname) return
		previousPath.current = pathname
		closeDetails()
		setChatsOpen(false)
	}, [pathname, closeDetails])

	// Two sheets never stand over one phone screen, and nothing here has to arrange that: below 768 px
	// a details view is itself a modal sheet, so while one is up the bar is out of the document's
	// accessible tree and takes no press — there is no gesture that could ask for the list from behind
	// it (measured: the entry is not reachable by role while the view's sheet is open).
	const openChats = useCallback(() => setChatsOpen(true), [])

	const detailsRegion = useRef<HTMLElement | null>(null)
	// F6 walks the regions that are on screen, in DOM order — read from the grid itself rather than
	// from a list this component keeps: the grid *is* the order, a region that is not drawn is not in
	// it, and a walk over a stale list would stop at a pane that had gone.
	const cycle = useCallback((step: 1 | -1) => {
		const present = [...document.querySelectorAll<HTMLElement>(`.${GRID} > .${REGION}`)]
		if (present.length === 0) return
		const at = present.findIndex((element) => element.contains(document.activeElement))
		const next = present[(at + step + present.length) % present.length]
		next?.focus()
	}, [])

	// Opening the column moves the reader into it; the view may move the focus further (a thread
	// focuses its composer). Closing it gives the focus back, which is details.tsx's business.
	//
	// What takes the focus is the pane's own heading and not the column: focusing the region made it
	// match `:focus-visible`, so opening a profile painted a 2 px accent outline around the whole
	// 360 px column, which reads as an error state (X-A3 P11). The heading is the right target in
	// any case — it is what the reader has just asked to see.
	//
	// A column the page opens rather than a press — an AI chat's panel named in the address, which
	// comes with the chat — is not focused: the reader is reading the page, and moving the caret out
	// of it to announce the panel answers a question nobody put. Escape closes it either way.
	//
	// Escape belongs to the region rather than to any element in it, and it is answered only when
	// nothing inside has answered it already — a menu closing itself, an autocomplete list.
	const openRequest = details.request
	const quietly = details.quiet
	useEffect(() => {
		const region = detailsRegion.current
		if (openRequest === null || !hasDetails || region === null) return
		if (!quietly) {
			const heading = region.querySelector<HTMLElement>(".tb-details__title")
			;(heading ?? region).focus()
		}
		function leave(event: KeyboardEvent) {
			if (event.key !== "Escape" || event.defaultPrevented) return
			event.preventDefault()
			closeDetails()
		}
		region.addEventListener("keydown", leave)
		return () => region.removeEventListener("keydown", leave)
	}, [openRequest, quietly, hasDetails, closeDetails])

	const sidebar = useResizablePanel({
		storageKey: SIDEBAR_WIDTH_KEY,
		defaultWidth: SIDEBAR.default,
		minWidth: SIDEBAR.min,
		maxWidth: SIDEBAR.max,
		side: "start",
		label: t("shell.resize.sidebar"),
	})
	const detailsPanel = useResizablePanel({
		storageKey: DETAILS_WIDTH_KEY,
		defaultWidth: DETAILS.default,
		minWidth: DETAILS.min,
		maxWidth: DETAILS.max,
		side: "end",
		label: t("shell.resize.details"),
	})

	// Core's own bindings, plus everything the modules registered. They are built once: the
	// registry indexes them, and rebuilding it on every render would drop a pressed key.
	const moduleShortcuts = useSlot("shortcuts")
	const defs = useMemo<ShortcutDef[]>(
		() => [
			...coreShortcuts({
				openQuickSwitcher: () => setSwitcherOpen(true),
				openShortcutSheet: () => setSheetOpen(true),
				goToSearch: () => void navigate({ to: "/search" }),
				goToActivity: () => void navigate({ to: "/activity" }),
				cycleRegions: (step) => {
					cycle(step)
				},
				goBack: () => router.history.back(),
			}),
			...moduleShortcuts,
		],
		[navigate, router, cycle, moduleShortcuts],
	)
	// This account's own remapping of any of them, merged over the device's copy inside the provider.
	// It is a setting rather than a device preference, so the keys somebody chose follow them to the
	// next machine (G-51); no screen writes it this round.
	const overrides = useModel((model) => selectSetting(model, "core.shell.shortcut_overrides"))

	// The grid names only the columns that exist. A resizable column is one track and nothing more:
	// its handle is inside it, taken out of the flow and centred on the border (X-A3 V19), so a pane
	// that is not there takes no track and F6 never stops at a region that is not there.
	const columns =
		mode === "narrow"
			? "1fr"
			: [
					"auto",
					hasSidebar ? `${sidebar.width}px` : "",
					"1fr",
					hasDetails ? `${detailsPanel.width}px` : "",
				]
					.filter((part) => part !== "")
					.join(" ")

	return (
		<ActiveConversationProvider value={{ id: activeConversation, set: setActiveConversation }}>
			<SidebarFoldContext.Provider value={fold}>
				<BottomBarContext.Provider value={stepAside}>
					<ShortcutProvider defs={defs} overrides={overrides}>
						<div className={`tb-shell tb-shell--${mode}`}>
							{/* One skip link here, past the rail and the sidebar into the main pane. The
							    screen inside it adds its own — a conversation offers "to the message list"
							    and "to the composer" (COMPOSER_ID), because it owns both ends of those and
							    a link to an element that is not on this route is worse than no link. */}
							<a className="tb-skip-link" href={`#${MAIN_ID}`}>
								{t("shell.skip.toMain")}
							</a>
							<ConnectionBanner />
							<div className={GRID} style={{ gridTemplateColumns: columns }}>
								{hasRail ? (
									<div
										className={`${REGION} tb-region--rail`}
										tabIndex={-1}
										// Below 768 px the rail is the bottom bar, and a toast stands above it
										// rather than over it (`ToastHost.tsx`).
										{...(mode === "narrow" ? { [TOAST_FLOOR]: "" } : {})}
									>
										<Rail onOpenChats={openChats} />
									</div>
								) : null}
								{hasSidebar ? (
									<div id={SIDEBAR_ID} className={`${REGION} tb-region--sidebar`} tabIndex={-1}>
										<Sidebar />
										{/* Inside the region it resizes, not beside it: a handle in the grid was the
										    one node in the application outside every landmark (X-A3 V19). */}
										<ResizeHandle {...sidebar.handleProps} />
									</div>
								) : null}
								{/* The folded column brought back over the page. It is in the grid's DOM order
								    — after the rail, before the page — so F6 and a screen reader meet it where
								    the column stands, but it takes no track: the page keeps its width and not a
								    line of the text moves. It has no handle; it is as wide as the column the
								    person dragged, and a look at a list is not the moment to resize it. */}
								{fold.state === "overlay" ? (
									<div
										id={SIDEBAR_ID}
										ref={overlayRegion}
										className={`${REGION} tb-region--sidebar tb-region--overlay`}
										style={{ inlineSize: `${String(sidebar.width)}px` }}
										tabIndex={-1}
									>
										<div className="tb-overlay-head">
											<Button
												variant="ghost"
												size="sm"
												before={<Icon name="panelLeft" size={16} />}
												label={t("shell.sidebar.keepOpen")}
												onClick={() => {
													keepColumn()
													// The button goes with the look; the focus follows the list into
													// the column that now stands where the look was.
													requestAnimationFrame(() => {
														document.getElementById(SIDEBAR_ID)?.focus({ preventScroll: true })
													})
												}}
											/>
										</div>
										<Sidebar />
									</div>
								) : null}
								<main
									id={MAIN_ID}
									className={`${REGION} tb-region--main`}
									tabIndex={-1}
									aria-label={t("shell.region.main")}
								>
									{children}
								</main>
								{hasDetails && openView !== undefined ? (
									<div
										className={`${REGION} tb-region--details`}
										tabIndex={-1}
										ref={(element) => {
											detailsRegion.current = element
										}}
									>
										<ResizeHandle {...detailsPanel.handleProps} />
										<DetailsPane
											view={openView}
											params={details.request?.params ?? {}}
											onClose={() => closeDetails()}
										/>
									</div>
								) : null}
							</div>
							{/* Below 1200 px the column is a sheet at the end edge, titled the way the column
							    is headed: by the view's group, so About, Files and Pinned are one "Conversation
							    details" in both layouts. */}
							{mode !== "wide" && openView !== undefined ? (
								<Dialog
									open
									sheet
									onOpenChange={(open) => {
										if (!open) closeDetails()
									}}
									title={translate(openView.groupTitleKey ?? openView.titleKey)}
								>
									<DetailsPane view={openView} params={details.request?.params ?? {}} />
								</Dialog>
							) : null}
							{/* The conversation list where it has no column: the sidebar itself, in the details
							    column's sheet, titled the way the entry that opened it is named. It is drawn in
							    the one layout that has no column for it — a window that grows past 768 px has the
							    column back and this is simply not on the screen; a window that shrinks again is a
							    reader returning to the layout they left, with the list they left open. */}
							{mode === "narrow" ? (
								<Dialog
									open={chatsOpen}
									sheet
									onOpenChange={setChatsOpen}
									title={t("shell.nav.chats")}
								>
									{/* A row is a link, and choosing one is the reader asking to be taken there: the
									    sheet has done its job. A navigation closes it through the pathname effect
									    above; this is what answers for the one row whose address does not change —
									    the conversation the reader is already in. */}
									{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- the rows inside are the controls, and a keyboard's Enter on one of them fires this same click */}
									<div
										className="tb-sidebar-sheet"
										onClick={(event) => {
											if ((event.target as HTMLElement).closest("a") !== null) setChatsOpen(false)
										}}
									>
										<Sidebar />
									</div>
								</Dialog>
							) : null}
							<QuickSwitcher open={switcherOpen} onOpenChange={setSwitcherOpen} />
							<ShortcutSheet open={sheetOpen} onOpenChange={setSheetOpen} />
						</div>
					</ShortcutProvider>
				</BottomBarContext.Provider>
			</SidebarFoldContext.Provider>
		</ActiveConversationProvider>
	)
}

/**
 * The column's fold on this address (G-02 §2, S15): where it rests, whether somebody is looking at
 * it over the page, and the toggle the page's band draws.
 *
 * The person's choices are this tab's state, seeded from the device (`readFoldChoice`): a press is
 * answered in the same frame, and a browser that refuses storage still keeps the choice for as long
 * as the tab lives. The look — the overlay — belongs to the address it was opened on and is not a
 * state that has to be cleared: on the next address the column simply rests again, in the render
 * that draws that address, so the list never stands over a page it was not opened on.
 */
function useColumnFold(
	sections: readonly SidebarSection[],
	place: string,
	pathname: string,
): { fold: SidebarFold; keep: () => void } {
	const rule = foldRuleOf(sections)
	const roomy = useWindowAtLeast(rule?.belowPx)
	const [chosen, setChosen] = useState<Readonly<Record<string, FoldChoice>>>({})
	const choice = chosen[place] ?? readFoldChoice(place)
	// A rule may answer from the model — the drive knows a document's address once the item has
	// arrived, which on a fresh load is after the first render — so it is asked through the store
	// and the column folds when the answer changes, not only on the next navigation.
	const onHere = useModel(() => rule !== undefined && rule.on(pathname))
	const resting = restingColumn(onHere ? rule : undefined, pathname, roomy, choice)
	const [lookingAt, setLookingAt] = useState<string | null>(null)
	// A column that comes to rest open — the window grew past the rule's width — ends the look, so a
	// window narrowed again does not bring the list back over the page by itself.
	if (resting === "docked" && lookingAt !== null) setLookingAt(null)
	const state: ColumnState =
		resting === "docked" ? "docked" : lookingAt === pathname ? "overlay" : "folded"
	const available = rule?.on(pathname) === true

	const toggle = useCallback(() => {
		const { next, remember } = toggledColumn(state, roomy)
		if (remember !== undefined) {
			setChosen((before) => ({ ...before, [place]: remember }))
			writeFoldChoice(place, remember)
		}
		setLookingAt(next === "overlay" ? pathname : null)
	}, [state, roomy, place, pathname, setChosen, setLookingAt])

	// The look's own *Keep the list open*: the column stands beside the page from now on, at every
	// width, remembered as a toggle that shows a standing column is. Only the shell
	// draws that button, so it is handed back beside the fold and not inside it.
	const keep = useCallback(() => {
		setChosen((before) => ({ ...before, [place]: "open" }))
		writeFoldChoice(place, "open")
		setLookingAt(null)
	}, [place, setChosen, setLookingAt])

	const fold = useMemo(
		() => ({ available, state, expanded: state !== "folded", toggle, controls: SIDEBAR_ID }),
		[available, state, toggle],
	)
	return { fold, keep }
}

/**
 * What the overlay does while it is up, as a ref for its region. It is a look at a list, so it
 * behaves like one: the focus goes into it — onto the find field where the column has one, because
 * somebody who opens a list of pages is usually about to look for one — and it goes away on Escape
 * (the focus then returns to whatever opened it), on a press or a focus anywhere else in the frame,
 * and on choosing a row.
 *
 * "Anywhere else in the frame" is deliberate. A menu, a dialog or a toast that a row opens is drawn
 * outside the frame, in a layer of its own; a press there is a press *in* the list's business, and
 * closing the list under it would take the menu with it. And the control that toggles the column is
 * known by the id it names (`SidebarFold.controls`): a press on it is its own, so it closes the
 * overlay once instead of closing it here and opening it again in its click.
 */
function useOverlayColumn(fold: SidebarFold): (element: HTMLDivElement | null) => void {
	const [region, setRegion] = useState<HTMLElement | null>(null)
	const close = fold.toggle

	// Into the list when it appears, and only then: the effect below re-subscribes whenever the
	// toggle is rebuilt, and taking the focus again each time would pull it out of the find field.
	const opener = useRef<Element | null>(null)
	useEffect(() => {
		if (region === null) return
		opener.current = document.activeElement
		return focusFieldOf(region)
	}, [region])

	useEffect(() => {
		if (region === null) return
		const list = region
		const frame = list.parentElement
		const isToggle = (element: Element) =>
			element.closest(`[aria-controls="${SIDEBAR_ID}"]`) !== null
		const elsewhereInFrame = (element: Element) =>
			!list.contains(element) && !isToggle(element) && frame?.contains(element) === true

		function onPointerDown(event: PointerEvent) {
			if (event.target instanceof Element && elsewhereInFrame(event.target)) close()
		}
		function onFocusOut(event: FocusEvent) {
			if (event.relatedTarget instanceof Element && elsewhereInFrame(event.relatedTarget)) close()
		}
		// On the document and not on the region, so that Escape on the toggle itself is answered too;
		// one a menu inside the list has already answered is left alone.
		function onKeyDown(event: KeyboardEvent) {
			if (event.key !== "Escape" || event.defaultPrevented) return
			const at = document.activeElement
			if (at !== null && !list.contains(at) && !isToggle(at)) return
			event.preventDefault()
			close()
			const back = opener.current
			if (back instanceof HTMLElement && back.isConnected) back.focus({ preventScroll: true })
		}
		// A row is a link. Choosing one mostly changes the address, which ends the look by itself;
		// this answers for the row of the page that is already open.
		function onClick(event: MouseEvent) {
			if (event.target instanceof Element && event.target.closest("a[href]") !== null) close()
		}

		document.addEventListener("pointerdown", onPointerDown, true)
		document.addEventListener("keydown", onKeyDown)
		list.addEventListener("focusout", onFocusOut)
		list.addEventListener("click", onClick)
		return () => {
			document.removeEventListener("pointerdown", onPointerDown, true)
			document.removeEventListener("keydown", onKeyDown)
			list.removeEventListener("focusout", onFocusOut)
			list.removeEventListener("click", onClick)
		}
	}, [region, close])

	return setRegion
}

/** How long the overlay waits for its rows to bring a find field before it leaves the focus be. */
const FIELD_WAIT_MS = 1_500

/**
 * Puts the focus into a column that has just come over the page: onto its find field, or onto the
 * column itself until the field is there.
 *
 * The rows of a section may be a chunk of their own that the first look at the list is what fetches
 * (`SlotComponent`), so the field often arrives a moment after the column. The column holds the
 * focus meanwhile — the keyboard is in the list either way — and the field takes it over when it
 * appears, unless the person has moved on by then; the wait is bounded, so a column without a field
 * is not watched for one. The returned function ends the wait.
 */
function focusFieldOf(region: HTMLElement): () => void {
	const fieldIn = () => region.querySelector<HTMLElement>("input:not([disabled])")
	const field = fieldIn()
	if (field !== null) {
		field.focus({ preventScroll: true })
		return () => undefined
	}
	region.focus({ preventScroll: true })
	const watch = new MutationObserver(() => {
		const arrived = fieldIn()
		if (arrived === null) return
		stop()
		if (document.activeElement === region) arrived.focus({ preventScroll: true })
	})
	const timer = setTimeout(() => stop(), FIELD_WAIT_MS)
	function stop() {
		watch.disconnect()
		clearTimeout(timer)
	}
	watch.observe(region, { childList: true, subtree: true })
	return stop
}
